import { apiFetch } from "../../../shared/apiClient";
import {
  FACTSET_ID_LOOKUP_CLIENT_TIMEOUT_MS,
  FACTSET_ID_LOOKUP_ERROR_CODES,
  FACTSET_ROUTES,
  REAUTH_REQUIRED_OAUTH_ERROR_CODES,
} from "../../../shared/factset/factsetConstants";
import { isAbortError } from "../../../shared/utils/abortError";

export type IdLookupDataset = "equities" | "privateCompanies";
export type IdLookupStatus = "ok" | "no_match" | "partial" | "error";
export type ParentEnrichmentStatus =
  | "complete"
  | "partial"
  | "not_applicable"
  | "unavailable";

export interface IdLookupFilterBlock {
  equityType: string[] | null;
  region: string[] | null;
}

export interface IdLookupFilters {
  include: IdLookupFilterBlock | null;
  exclude: IdLookupFilterBlock | null;
}

export interface IdLookupRequest {
  search_text: string;
  filters: IdLookupFilters | null;
  limit: number;
}

export interface IdLookupCandidate {
  rank: number;
  id: string;
  id_type: string | null;
  dataset: IdLookupDataset;
  company_type: "public" | "private";
  name: string;
  matched_name: string | null;
  issuer_name: string | null;
  ticker: string | null;
  entity_id: string | null;
  security_type: string | null;
  country: string | null;
  website: string | null;
  ultimate_parent_entity_id: string | null;
  ultimate_parent_company_name: string | null;
}

export interface IdLookupResponseMeta {
  lookup_request_id?: string;
  id_lookup_retry_count?: number;
  entity_reference_retry_count?: number;
  duration_ms?: number;
}

export interface IdLookupResponse {
  success: boolean;
  status: IdLookupStatus;
  search_text: string;
  datasets: IdLookupDataset[];
  candidate_count: number;
  candidates: IdLookupCandidate[];
  parent_enrichment_status: ParentEnrichmentStatus;
  fallback_recommended: boolean;
  fallback_reason: string | null;
  error_code: string | null;
  error: string | null;
  meta?: IdLookupResponseMeta;
}

interface ParsedApiError {
  error?: string;
  message?: string;
  details?: unknown;
  reauthorizationUrl?: string;
}

export class IdLookupReauthRequiredError extends Error {
  reauthorizationUrl: string;
  serverErrorCode: string;

  constructor(message: string, reauthorizationUrl: string, serverErrorCode: string) {
    super(message);
    this.name = "IdLookupReauthRequiredError";
    this.reauthorizationUrl = reauthorizationUrl;
    this.serverErrorCode = serverErrorCode;
    Object.setPrototypeOf(this, IdLookupReauthRequiredError.prototype);
  }
}

export class IdLookupFailedError extends Error {
  serverErrorCode?: string;
  httpStatus?: number;
  details?: unknown;

  constructor(
    message: string,
    options: { serverErrorCode?: string; httpStatus?: number; details?: unknown } = {},
  ) {
    super(message);
    this.name = "IdLookupFailedError";
    this.serverErrorCode = options.serverErrorCode;
    this.httpStatus = options.httpStatus;
    this.details = options.details;
    Object.setPrototypeOf(this, IdLookupFailedError.prototype);
  }
}

export class IdLookupTimeoutError extends Error {
  constructor(message = "FactSet company lookup timed out.") {
    super(message);
    this.name = "IdLookupTimeoutError";
    Object.setPrototypeOf(this, IdLookupTimeoutError.prototype);
  }
}

export interface IdLookupRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

function abortError(message: string): DOMException | Error {
  if (typeof DOMException === "function") {
    return new DOMException(message, "AbortError");
  }
  const error = new Error(message);
  error.name = "AbortError";
  return error;
}

function withTimeoutSignal(
  signal: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; cleanup: () => void; didTimeout: () => boolean } {
  const controller = new AbortController();
  let timedOut = false;
  const onAbort = () =>
    controller.abort((signal as AbortSignal & { reason?: unknown })?.reason);
  if (signal?.aborted) {
    controller.abort((signal as AbortSignal & { reason?: unknown }).reason);
  } else {
    signal?.addEventListener("abort", onAbort, { once: true });
  }
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
    },
    didTimeout: () => timedOut,
  };
}

async function readApiError(response: Response): Promise<ParsedApiError> {
  try {
    const body = await response.clone().json();
    if (body && typeof body === "object") return body as ParsedApiError;
  } catch {
    // Fall through to text.
  }
  try {
    const text = await response.text();
    return { message: text || `HTTP ${response.status}` };
  } catch {
    return { message: `HTTP ${response.status}` };
  }
}

async function throwForResponse(response: Response): Promise<never> {
  const body = await readApiError(response);
  const code = typeof body.error === "string" ? body.error : "";
  const message = body.message || `FactSet company lookup failed (${response.status})`;

  if (REAUTH_REQUIRED_OAUTH_ERROR_CODES.has(code as never)) {
    throw new IdLookupReauthRequiredError(
      message,
      typeof body.reauthorizationUrl === "string" && body.reauthorizationUrl
        ? body.reauthorizationUrl
        : FACTSET_ROUTES.OAUTH_START,
      code,
    );
  }
  if (
    code === FACTSET_ID_LOOKUP_ERROR_CODES.TIMEOUT ||
    response.status === 504
  ) {
    throw new IdLookupTimeoutError(message);
  }
  throw new IdLookupFailedError(message, {
    serverErrorCode: code || undefined,
    httpStatus: response.status,
    details: body.details,
  });
}

function nullableString(value: unknown): string | null | undefined {
  if (value === null) return null;
  if (typeof value === "string") return value;
  return undefined;
}

function normalizeCandidate(value: unknown): IdLookupCandidate | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  const dataset = candidate.dataset;
  const companyType = candidate.company_type;
  if (
    !Number.isInteger(candidate.rank) ||
    typeof candidate.id !== "string" ||
    !candidate.id ||
    (dataset !== "equities" && dataset !== "privateCompanies") ||
    (companyType !== "public" && companyType !== "private") ||
    typeof candidate.name !== "string" ||
    !candidate.name
  ) {
    return null;
  }

  const stringFields = [
    "id_type",
    "matched_name",
    "issuer_name",
    "ticker",
    "entity_id",
    "security_type",
    "country",
    "website",
    "ultimate_parent_entity_id",
    "ultimate_parent_company_name",
  ] as const;
  const normalized = {} as Record<(typeof stringFields)[number], string | null>;
  for (const field of stringFields) {
    const result = nullableString(candidate[field]);
    if (result === undefined) return null;
    normalized[field] = result;
  }

  return {
    rank: candidate.rank as number,
    id: candidate.id,
    id_type: normalized.id_type,
    dataset,
    company_type: companyType,
    name: candidate.name,
    matched_name: normalized.matched_name,
    issuer_name: normalized.issuer_name,
    ticker: normalized.ticker,
    entity_id: normalized.entity_id,
    security_type: normalized.security_type,
    country: normalized.country,
    website: normalized.website,
    ultimate_parent_entity_id: normalized.ultimate_parent_entity_id,
    ultimate_parent_company_name: normalized.ultimate_parent_company_name,
  };
}

function normalizeResponse(payload: unknown): IdLookupResponse {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new IdLookupFailedError("Company lookup returned an unexpected response shape.", {
      serverErrorCode: FACTSET_ID_LOOKUP_ERROR_CODES.INVALID_RESPONSE,
    });
  }
  const data = payload as Record<string, unknown>;
  const statuses = new Set<IdLookupStatus>(["ok", "no_match", "partial", "error"]);
  const parentStatuses = new Set<ParentEnrichmentStatus>([
    "complete",
    "partial",
    "not_applicable",
    "unavailable",
  ]);
  if (
    data.success !== true ||
    typeof data.status !== "string" ||
    !statuses.has(data.status as IdLookupStatus) ||
    typeof data.search_text !== "string" ||
    !Array.isArray(data.datasets) ||
    !Array.isArray(data.candidates) ||
    !Number.isInteger(data.candidate_count) ||
    typeof data.parent_enrichment_status !== "string" ||
    !parentStatuses.has(data.parent_enrichment_status as ParentEnrichmentStatus) ||
    typeof data.fallback_recommended !== "boolean"
  ) {
    throw new IdLookupFailedError("Company lookup returned an unexpected response shape.", {
      serverErrorCode: FACTSET_ID_LOOKUP_ERROR_CODES.INVALID_RESPONSE,
    });
  }
  if (
    data.datasets.some(
      (dataset) => dataset !== "equities" && dataset !== "privateCompanies",
    )
  ) {
    throw new IdLookupFailedError("Company lookup returned an unknown dataset.", {
      serverErrorCode: FACTSET_ID_LOOKUP_ERROR_CODES.INVALID_RESPONSE,
    });
  }
  const candidates = data.candidates.map(normalizeCandidate);
  if (
    candidates.some((candidate) => candidate === null) ||
    data.candidate_count !== candidates.length
  ) {
    throw new IdLookupFailedError("Company lookup returned malformed candidates.", {
      serverErrorCode: FACTSET_ID_LOOKUP_ERROR_CODES.INVALID_RESPONSE,
    });
  }

  return {
    success: true,
    status: data.status as IdLookupStatus,
    search_text: data.search_text,
    datasets: data.datasets as IdLookupDataset[],
    candidate_count: data.candidate_count as number,
    candidates: candidates as IdLookupCandidate[],
    parent_enrichment_status:
      data.parent_enrichment_status as ParentEnrichmentStatus,
    fallback_recommended: data.fallback_recommended,
    fallback_reason: nullableString(data.fallback_reason) ?? null,
    error_code: nullableString(data.error_code) ?? null,
    error: nullableString(data.error) ?? null,
    meta:
      data.meta && typeof data.meta === "object" && !Array.isArray(data.meta)
        ? (data.meta as IdLookupResponseMeta)
        : undefined,
  };
}

export async function executeFactSetIdLookup(
  request: IdLookupRequest,
  options: IdLookupRequestOptions = {},
): Promise<IdLookupResponse> {
  const timeoutMs = options.timeoutMs ?? FACTSET_ID_LOOKUP_CLIENT_TIMEOUT_MS;
  const timeout = withTimeoutSignal(options.signal, timeoutMs);
  try {
    const response = await apiFetch(FACTSET_ROUTES.COMPANY_LOOKUP, {
      method: "POST",
      body: JSON.stringify(request),
      signal: timeout.signal,
    });
    if (!response.ok) {
      await throwForResponse(response);
    }
    return normalizeResponse(await response.json());
  } catch (error) {
    if (isAbortError(error)) {
      if (options.signal?.aborted) {
        throw abortError("Company lookup cancelled by caller.");
      }
      if (timeout.didTimeout()) {
        throw new IdLookupTimeoutError(
          `FactSet company lookup timed out after ${timeoutMs} ms.`,
        );
      }
    }
    throw error;
  } finally {
    timeout.cleanup();
  }
}

export const _test = {
  normalizeCandidate,
  normalizeResponse,
};
